import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

export default function LandingEstateAgentsPage() {
  return (
    <main data-testid="landing-estate-agents" className="pt-24">
      <Helmet>
        <title>Virtual Staging for Estate Agents | The Secret Seller</title>
        <meta name="description" content="Professional virtual staging for UK estate agents. Turn empty listings into buyer-ready homes in 48 hours. From £55 per image. Trusted by Savills and leading agents." />
        <link rel="canonical" href={`${PROD_DOMAIN}/virtual-staging-estate-agents`} />
      </Helmet>

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">For Estate Agents</p>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-6">
                Virtual staging that helps your listings sell faster
              </h1>
              <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-8">
                Empty properties sit on the market longer and attract fewer enquiries. Virtual staging transforms your listing photos into beautifully presented spaces that help buyers connect with a property — from the very first click on Rightmove or Zoopla.
              </p>
              <Link to="/order" className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all">
                Send Us Your Photos <ArrowRight size={14} />
              </Link>
            </div>
            <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/fiv5p37c_Study%20-%20use%20one%20of%20these%20%281%29.jpg" alt="Virtually staged home office" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Why Estate Agents Use Virtual Staging */}
      <section className="py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">Why estate agents use virtual staging</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { title: "More clicks on portals", desc: "Staged photos receive up to 40% more clicks on Rightmove and Zoopla compared to empty room shots." },
              { title: "Faster time to offer", desc: "Properties with staged imagery sell up to 73% faster. Buyers can visualise a home's potential immediately." },
              { title: "Higher perceived value", desc: "Staged properties are perceived as worth up to 20% more. Help your sellers achieve stronger offers." },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-3">{item.title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="py-24 lg:py-32 bg-[#FAFAF8]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">Before and after</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/v5bxzux5_8568101-interior10.jpg" alt="Empty bedroom before staging" className="w-full h-auto" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mt-3 text-center">Before</p>
            </div>
            <div>
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/yi5zide3_bed1.jpg" alt="Staged bedroom" className="w-full h-auto" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#0D2D54] mt-3 text-center">After</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-6">Simple, transparent pricing</h2>
          <div className="space-y-2 mb-8">
            <p className="text-[#0D2D54] text-lg"><span className="font-['Outfit'] text-3xl font-light">£65</span> per image</p>
            <p className="text-[#758C7B] text-sm font-medium">£55 per image for 10+ images</p>
          </div>
          <ul className="flex flex-wrap justify-center gap-6 text-sm text-[#6B7280] mb-10">
            {["Delivered in 48 hours", "No subscription", "Only pay for what you need"].map((item, i) => (
              <li key={i} className="flex items-center gap-2"><Check size={14} className="text-[#758C7B]" />{item}</li>
            ))}
          </ul>
          <Link to="/order" className="inline-flex items-center gap-3 bg-[#0D2D54] text-white px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-[#1A3D6B] transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">Frequently asked questions</h2>
          <div className="space-y-3">
            {[
              { q: "How quickly can I receive my staged images?", a: "We deliver within 48 hours of receiving your photos." },
              { q: "What quality photos do I need to send?", a: "Standard property listing photos work perfectly. We recommend well-lit, wide-angle shots taken with any decent camera or smartphone." },
              { q: "Can I choose the staging style?", a: "Yes. We offer modern, luxury, neutral, family, Scandinavian and other styles. Simply let us know your preference when ordering." },
              { q: "Is virtual staging legal for property listings?", a: "Yes, virtual staging is widely accepted in UK property marketing. Best practice is to note that images are virtually staged, which most portals now support." },
              { q: "Do you offer bulk pricing for agencies?", a: "Yes. Orders of 10+ images receive an automatic discount to £55 per image. For ongoing agency partnerships, contact us directly." },
            ].map((faq, i) => (
              <details key={i} className="border border-[#E8E8E4] group">
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-medium text-[#0D2D54] list-none">
                  <span className="pr-4">{faq.q}</span>
                  <span className="text-[#6B7280] group-open:rotate-90 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-[#6B7280] leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">Ready to transform your listings?</h2>
          <p className="text-[15px] text-white/50 mb-10">Send us your empty room photos and we'll return beautifully staged images within 48 hours.</p>
          <Link to="/order" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
